import { useTranslation } from 'react-i18next';
import {
  RadialBar,
  RadialBarChart,
  PolarRadiusAxis,
  Label,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useConversationRatingData } from '../hooks/useConversationRatingData.hook';
import {
  Card,
  CardHeader,
  CardDescription,
  CardTitle,
  CardContent,
  CardFooter,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';
import { DateRangeState } from '@/Domain/types/DateRange.types';

type Props = {
  range: DateRangeState;
};

const formatPercent = (count: number, total: number) =>
  total === 0 ? 0 : Math.round((count / total) * 100);

export const FeedbackChartsSection = ({ range }: Props) => {
  const { t } = useTranslation('insights_page');

  const {
    isLoading,
    chatExperienceData,
    chatExperienceTotal,
    chatExperienceConfig,
    aiHelpfulData,
    aiHelpfulTotal,
    aiHelpfulConfig,
    whyNotHelpfulData,
    whyNotHelpfulConfig,
  } = useConversationRatingData(range);

  // Check if there's data for each chart type
  const hasChatExperienceData =
    chatExperienceTotal > 0 &&
    chatExperienceData.length > 0 &&
    chatExperienceData.some((d) => d.positive > 0 || d.negative > 0);
  const hasAiHelpfulData =
    aiHelpfulTotal > 0 &&
    aiHelpfulData.length > 0 &&
    aiHelpfulData.some((d) => d.yes > 0 || d.no > 0);
  const hasWhyNotHelpfulData =
    whyNotHelpfulData && whyNotHelpfulData.length > 0 && whyNotHelpfulData.some((d) => d.count > 0);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chat experience */}
        <Card className="flex flex-col">
          <CardHeader className="pb-0">
            <CardTitle>{t('conversation_rating.chat_experience')}</CardTitle>
            <CardDescription>{t('general.distribution_over_time')}</CardDescription>
          </CardHeader>
          {!hasChatExperienceData ? (
            <CardContent className="flex flex-1 items-center justify-center pb-0">
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            </CardContent>
          ) : (
            <>
              <CardContent className="flex flex-1 items-center pb-0">
                <ChartContainer
                  config={chatExperienceConfig}
                  className={`mx-auto aspect-square w-full max-w-[250px] ${isLoading ? 'animate-pulse' : ''}`}
                >
                  <RadialBarChart
                    data={chatExperienceData}
                    endAngle={180}
                    innerRadius={80}
                    outerRadius={130}
                  >
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                      <Label
                        content={({ viewBox }) => {
                          if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                            return (
                              <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) - 16}
                                  className="fill-foreground text-2xl font-bold"
                                >
                                  {chatExperienceTotal.toLocaleString()}
                                </tspan>
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) + 4}
                                  className="fill-muted-foreground"
                                >
                                  {t('conversation_rating.responses')}
                                </tspan>
                              </text>
                            );
                          }
                        }}
                      />
                    </PolarRadiusAxis>
                    <RadialBar
                      dataKey="positive"
                      stackId="a"
                      cornerRadius={5}
                      fill="var(--color-positive)"
                      className="stroke-transparent stroke-2"
                    />
                    <RadialBar
                      dataKey="negative"
                      fill="var(--color-negative)"
                      stackId="a"
                      cornerRadius={5}
                      className="stroke-transparent stroke-2"
                    />
                  </RadialBarChart>
                </ChartContainer>
              </CardContent>
              <CardFooter className="flex-col gap-1.5 text-xs -mt-14 w-full max-w-[260px] mx-auto">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: chatExperienceConfig.negative.color }}
                    />
                    <span>{chatExperienceConfig.negative.label}</span>
                  </div>
                  <span className="text-muted-foreground tabular-nums">
                    {chatExperienceData[0].negative.toLocaleString()} (
                    {formatPercent(chatExperienceData[0].negative, chatExperienceTotal)}%)
                  </span>
                </div>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: chatExperienceConfig.positive.color }}
                    />
                    <span>{chatExperienceConfig.positive.label}</span>
                  </div>
                  <span className="text-muted-foreground tabular-nums">
                    {chatExperienceData[0].positive.toLocaleString()} (
                    {formatPercent(chatExperienceData[0].positive, chatExperienceTotal)}%)
                  </span>
                </div>
              </CardFooter>
            </>
          )}
        </Card>

        {/* AI helpful */}
        <Card className="flex flex-col">
          <CardHeader className="pb-0">
            <CardTitle>{t('conversation_rating.ai_helpful')}</CardTitle>
            <CardDescription>{t('general.distribution')}</CardDescription>
          </CardHeader>
          {!hasAiHelpfulData ? (
            <CardContent className="flex flex-1 items-center justify-center pb-0">
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            </CardContent>
          ) : (
            <>
              <CardContent className="flex flex-1 items-center pb-0">
                <ChartContainer
                  config={aiHelpfulConfig}
                  className={`mx-auto aspect-square w-full max-w-[250px] ${isLoading ? 'animate-pulse' : ''}`}
                >
                  <RadialBarChart
                    data={aiHelpfulData}
                    endAngle={180}
                    innerRadius={80}
                    outerRadius={130}
                  >
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                      <Label
                        content={({ viewBox }) => {
                          if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                            return (
                              <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) - 16}
                                  className="fill-foreground text-2xl font-bold"
                                >
                                  {aiHelpfulTotal.toLocaleString()}
                                </tspan>
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) + 4}
                                  className="fill-muted-foreground"
                                >
                                  {t('conversation_rating.responses')}
                                </tspan>
                              </text>
                            );
                          }
                        }}
                      />
                    </PolarRadiusAxis>
                    <RadialBar
                      dataKey="yes"
                      stackId="a"
                      cornerRadius={5}
                      fill="var(--color-yes)"
                      className="stroke-transparent stroke-2"
                    />
                    <RadialBar
                      dataKey="no"
                      fill="var(--color-no)"
                      stackId="a"
                      cornerRadius={5}
                      className="stroke-transparent stroke-2"
                    />
                  </RadialBarChart>
                </ChartContainer>
              </CardContent>
              <CardFooter className="flex-col gap-1.5 text-xs -mt-14 w-full max-w-[260px] mx-auto">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: aiHelpfulConfig.no.color }}
                    />
                    <span>{aiHelpfulConfig.no.label}</span>
                  </div>
                  <span className="text-muted-foreground tabular-nums">
                    {aiHelpfulData[0].no.toLocaleString()} (
                    {formatPercent(aiHelpfulData[0].no, aiHelpfulTotal)}%)
                  </span>
                </div>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: aiHelpfulConfig.yes.color }}
                    />
                    <span>{aiHelpfulConfig.yes.label}</span>
                  </div>
                  <span className="text-muted-foreground tabular-nums">
                    {aiHelpfulData[0].yes.toLocaleString()} (
                    {formatPercent(aiHelpfulData[0].yes, aiHelpfulTotal)}%)
                  </span>
                </div>
              </CardFooter>
            </>
          )}
        </Card>
      </div>

      {/* Why not helpful - Bar Chart */}
      <Card className={isLoading ? 'animate-pulse' : ''}>
        <CardHeader>
          <CardTitle>{t('conversation_rating.why_not_helpful')}</CardTitle>
          <CardDescription>{t('general.distribution')}</CardDescription>
        </CardHeader>
        <CardContent>
          {!hasWhyNotHelpfulData ? (
            <ChartPlaceholder
              icon="mingcute:sad-line"
              titleTranslationKey="no_data_in_range.title"
              descriptionTranslationKey="no_data_in_range.description"
              spacing="small"
            />
          ) : (
            <ChartContainer
              config={whyNotHelpfulConfig}
              className={`h-56 w-full ${isLoading ? 'animate-pulse' : ''}`}
            >
              <BarChart accessibilityLayer data={whyNotHelpfulData} margin={{ left: 0, right: 0 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="reason"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) =>
                    whyNotHelpfulConfig[value as keyof typeof whyNotHelpfulConfig]?.label
                  }
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={false}
                  width={36}
                />
                <ChartTooltip
                  cursor={false}
                  wrapperStyle={{ whiteSpace: 'normal' }}
                  content={<ChartTooltipContent indicator="dot" className="w-auto" />}
                />
                <Bar dataKey="count" fill="var(--color-count)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
};
